import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useCommandPalette } from '../context/CommandPaletteContext';

interface SearchTriggerProps {
    placeholder?: string;
    /**
     * `bar` matches the app's 340px field, `wide` caps at 520px, `full` spans its
     * container — for when the field is the width of the panel holding it.
     */
    variant?: 'bar' | 'wide' | 'full';
    className?: string;
}

/**
 * The entry point into the ⌘K layer. Rendered as a fake input rather than a
 * real one so there is a single search implementation — the palette — instead
 * of a per-page field that filters a different list each time.
 *
 * Styled as the dashboard's `SearchBar` (white, h-11, rounded-xl, gray-200
 * border) so the resources section reads as the same product as the patients
 * and billing pages.
 */
export const SearchTrigger: React.FC<SearchTriggerProps> = ({
    placeholder = 'Search resources…',
    variant = 'bar',
    className = ''
}) => {
    const { open } = useCommandPalette();

    const widthClass =
        variant === 'full' ? 'w-full' : variant === 'wide' ? 'w-full max-w-[520px]' : 'w-[340px]';

    return (
        <button
            type="button"
            onClick={() => open()}
            className={`flex items-center h-11 bg-white border border-gray-200 rounded-xl px-4 text-left hover:border-gray-300 transition-colors ${widthClass} ${className}`}
        >
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
            <span className="flex-1 min-w-0 truncate text-gray-400">{placeholder}</span>
            <span className="ml-2 flex-shrink-0 text-xs font-medium text-gray-400">⌘K</span>
        </button>
    );
};

export default SearchTrigger;
